import { ObservableMap, action, computed, observable, runInAction } from "mobx";
import { Subscription } from "rxjs";

import QuickPlayStore, { QuickBetHomeTabs } from "./QuickPlayStore";

import { BetBoosterData } from "@data-types/bet-booster/index";
import { BetBoosterHubResponse } from "@api-types/bet-booster/BetBoosterHubResponse";
import { TBetBuilderPick } from "@data-types/bet-booster/BetBuilderPicks";
import { TMostPlayedTicket } from "@data-types/bet-booster/MostPlayedTickets";
import { EventsResultHubStore } from "./EventResultsHubStore";
import {
	resetIndicatorAfterDelay,
	updateSlipIndicator,
} from "@utils/bet-builder";
import { LoaderStore } from "@state/stores/common";
import { THEME_SETTINGS } from "@theme-settings";

export class BetBoosterStore {
	@observable hubData: BetBoosterData = {
		betBuilderPresetIds: [],
		betBuilderPresets: new Map<string, TBetBuilderPick>(),
		mostPlayedTicketIds: [],
		mostPlayedTickets: new Map<string, TMostPlayedTicket>(),
	};
	@observable isEmpty = false;
	@observable isInitialized = false;
	@observable isLoading = false;
	@observable private subscription: Subscription | null = null;
	@observable private timeoutMap: Map<string, ReturnType<typeof setTimeout>> =
		new Map();

	eventsResultHubStore: EventsResultHubStore;
	quickPlayStore: QuickPlayStore;
	loader: LoaderStore;
	counter = 0;
	constructor(store: QuickPlayStore) {
		this.quickPlayStore = store;
		this.loader = new LoaderStore();
	}

	@action.bound
	onInitialize() {
		if (!WEBPACK_IS_BET_BOOSTER_ENABLED) {
			return;
		}

		this.disposeSubscription();
		this.isLoading = true;
		this.subscription = this.quickPlayStore.rootStore.betBoosterHubStore.hub
			.getSubscription({
				subscriptionId: "bet-booster",
				shopId: this.quickPlayStore.rootStore.userAuthStore.shopId,
			})
			.subscribe({
				next: (hubData) => {
					this.handleHubResponse(hubData);
				},
				error: (e) => {
					console.error(e);
					this.isLoading = false;
					this.handleTabSelection();
				},
			});
	}

	@action.bound
	handleHubResponse(hubData: BetBoosterHubResponse) {
		if (Object.keys(hubData).length <= 1) {
			this.isEmpty = true;
			this.isLoading = false;

			return;
		}

		//#region  betbuilderpicks

		if (hubData.betBuilderPresetIds) {
			this.hubData.betBuilderPresetIds = hubData.betBuilderPresetIds;
		} else {
			this.hubData.betBuilderPresetIds = [];
			if (this.eventsResultHubStore?.initialized) {
				this.eventsResultHubStore.onDispose();
			}
		}

		if (this.hubData.betBuilderPresetIds.length) {
			let bbMap = new Map<string, TBetBuilderPick>();

			hubData.betBuilderPresets?.forEach((slip: TBetBuilderPick) => {
				const existingSlip = this.hubData.betBuilderPresets.get(
					slip.id
				);

				updateSlipIndicator(slip, existingSlip);

				const existingTimeoutId = this.timeoutMap.get(slip.id);
				if (existingTimeoutId) {
					clearTimeout(existingTimeoutId);
				}

				resetIndicatorAfterDelay(
					slip,
					3000,
					(updatedSlip) => {
						runInAction(() => {
							this.quickPlayStore.betBuilderPicksStore.updateBetBuilderPick(
								updatedSlip
							);
							this.hubData.betBuilderPresets.set(
								updatedSlip.id,
								updatedSlip
							);
						});
					},
					this.timeoutMap
				);

				bbMap.set(slip.id, slip);

				const isValidMultiOffer = slip.multiOffers.every(
					(mo) => mo.sportData != null
				);
				if (!isValidMultiOffer) {
					this.hubData.betBuilderPresetIds =
						this.hubData.betBuilderPresetIds.filter(
							(id) => slip.id != id
						);
				}
			});

			this.hubData.betBuilderPresets.forEach((oldSlip) => {
				if (!bbMap.has(oldSlip.id)) {
					bbMap.set(oldSlip.id, oldSlip);
				}
			});

			this.hubData.betBuilderPresets = bbMap;

			const eventIds = Array.from(this.hubData.betBuilderPresets.values())
				.filter((item) => item.isLive)
				.map((item) => item.eventId);

			if (!this.eventsResultHubStore?.initialized) {
				this.eventsResultHubStore = new EventsResultHubStore(
					eventIds,
					this.quickPlayStore.rootStore
				);
				this.eventsResultHubStore.onInitialize();
			}

			if (!this.isSubset(eventIds, this.eventsResultHubStore?.ids)) {
				this.eventsResultHubStore?.onDispose();
				this.eventsResultHubStore = new EventsResultHubStore(
					eventIds,
					this.quickPlayStore.rootStore
				);
				this.eventsResultHubStore.onInitialize();
			}
		} else {
			this.eventsResultHubStore?.onDispose();
			this.hubData.betBuilderPresets.clear();
		}

		//#endregion  betbuilderpicks

		//#region  mostplayed tickets
		if (hubData.mostPlayedTicketIds) {
			this.hubData.mostPlayedTicketIds = hubData.mostPlayedTicketIds;
		} else {
			this.hubData.mostPlayedTicketIds = [];
		}
		const resetMostPlayedTicketAfterDelay = (
			slip: TMostPlayedTicket,
			delay: number
		) => {
			const timeoutId = setTimeout(() => {
				runInAction(() => {
					slip.quotaIndicator = 0;
					slip.submitCountIndicator = false;

					this.quickPlayStore.mostPlayedTicketsStore.updateMostPlayedTicket(
						slip
					);
					this.hubData.mostPlayedTickets.set(slip.betSlipId, slip);
					this.timeoutMap.delete(slip.betSlipId);
				});
			}, delay);

			this.timeoutMap.set(slip.betSlipId, timeoutId);
		};

		if (this.hubData.mostPlayedTicketIds.length) {
			let mostPlayedMap = new Map<string, TMostPlayedTicket>();

			hubData.mostPlayedTickets?.forEach((slip: TMostPlayedTicket) => {
				const existingSlip = this.hubData.mostPlayedTickets.get(
					slip.betSlipId
				);

				if (existingSlip) {
					if (slip.maxCoefficient < existingSlip.maxCoefficient) {
						slip.quotaIndicator = -1;
					} else if (
						slip.maxCoefficient > existingSlip.maxCoefficient
					) {
						slip.quotaIndicator = 1;
					} else {
						slip.quotaIndicator = 0;
					}

					slip.submitCountIndicator =
						slip.submitCount > existingSlip.submitCount;

					const existingTimeoutId = this.timeoutMap.get(
						slip.betSlipId
					);
					if (existingTimeoutId) {
						clearTimeout(existingTimeoutId);
					}

					resetMostPlayedTicketAfterDelay(slip, 3500);
				}

				mostPlayedMap.set(slip.betSlipId, slip);
			});

			this.hubData.mostPlayedTickets.forEach((oldSlip) => {
				if (!mostPlayedMap.has(oldSlip.betSlipId)) {
					mostPlayedMap.set(oldSlip.betSlipId, oldSlip);
				}
			});

			this.hubData.mostPlayedTickets = mostPlayedMap;
		} else {
			this.hubData.mostPlayedTickets.clear();
		}

		this.quickPlayStore.betBuilderPicksStore.onInitializeBetBuilderPicks(
			this.hubData
		);

		this.quickPlayStore.mostPlayedTicketsStore.onInitializeMostPlayTickets();
		this.handleTabSelection();
		this.isInitialized = true;
		this.isLoading = false;
	}

	@action.bound
	handleTabSelection() {
		const options = THEME_SETTINGS.features.quickPlayOptions;

		const isBBPEnabled =
			this.quickPlayStore.isBetBuilderPicksAvailable &&
			options.includes("bet-builder-picks");
		const isMostPlayedEnabled =
			this.quickPlayStore.isMostPlayAvailable &&
			options.includes("most-played-events");
		const isBettingTipsEnabled =
			this.quickPlayStore.isBettingTipsAvailable &&
			options.includes("betting-tips");

		const setFirstAvailableTab = () => {
			if (isBBPEnabled) {
				this.quickPlayStore.setShouldDisplayHomeTabs(true);
				this.quickPlayStore.setActiveHomeTab(
					QuickBetHomeTabs.BET_BUILDER_PICKS
				);
			} else if (isMostPlayedEnabled) {
				this.quickPlayStore.setShouldDisplayHomeTabs(true);

				this.quickPlayStore.setActiveHomeTab(
					QuickBetHomeTabs.MOST_PLAYED
				);
			} else if (isBettingTipsEnabled) {
				this.quickPlayStore.setShouldDisplayHomeTabs(true);

				this.quickPlayStore.setActiveHomeTab(
					QuickBetHomeTabs.BETTING_TIPS
				);
			} else {
				this.quickPlayStore.setShouldDisplayHomeTabs(false);
				this.quickPlayStore.setActiveHomeTab(QuickBetHomeTabs.NONE);
			}
		};

		switch (this.quickPlayStore.activeHomeTab) {
			case QuickBetHomeTabs.BET_BUILDER_PICKS:
				if (!isBBPEnabled) setFirstAvailableTab();
				break;
			case QuickBetHomeTabs.MOST_PLAYED:
				if (!isMostPlayedEnabled) setFirstAvailableTab();
				break;
			case QuickBetHomeTabs.BETTING_TIPS:
				if (!isBettingTipsEnabled) setFirstAvailableTab();
				break;
			default:
				setFirstAvailableTab();
		}
	}

	@action.bound
	disposeSubscription() {
		if (this.subscription != null) {
			this.subscription.unsubscribe();
			this.subscription = null;
		}
	}

	@action.bound
	disposeResultsSubscription() {
		this.eventsResultHubStore?.onDispose();
	}

	@action.bound
	onDispose() {
		this.disposeSubscription();

		this.hubData.betBuilderPresetIds = [];
		this.hubData.betBuilderPresets.clear();
		this.hubData.mostPlayedTicketIds = [];
		this.hubData.mostPlayedTickets.clear();
		this.disposeResultsSubscription();
		this.timeoutMap.clear();
	}

	private isSubset(arr1: string[], arr2: string[]): boolean {
		const set2 = new Set(arr2);
		return arr1.every((value) => set2.has(value));
	}
}
